﻿// --- START OF FILE Contact.jsx ---

import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Mail, Clock, Send, UserCheck, BrainCircuit, Zap, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/use-toast';
import emailjs from '@emailjs/browser';

const Contact = () => {
  const form = useRef();
  const [isSending, setIsSending] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const serviceID = 'service_hlmqk0p'; 
  const templateID = 'template_c98v393'; 
  const publicKey = 'CBARzHSylhymCpKvM';

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSending(true);

    emailjs.sendForm(serviceID, templateID, form.current, publicKey)
      .then((result) => {
        setIsSubmitted(true);
      }, (error) => {
        toast({
          title: "Error Sending Message",
          description: "Something went wrong. Please try again or email me directly.",
          variant: "destructive",
        });
        console.error('EmailJS Error:', error);
      })
      .finally(() => {
        setIsSending(false);
      });
  };

  const contactInfo = [
    { icon: Mail, title: 'Email Us', details: 'support@vegavelox.com', description: 'Primary contact for all inquiries and partnerships.' },
    { icon: Clock, title: 'Availability', details: 'Mon - Fri / 10am - 9pm (IST)', description: 'Consulting globally from an Indian Standard time zone.' }
  ];

  const stats = [
    { icon: UserCheck, value: 'Direct', label: 'Founder Access' },
    { icon: BrainCircuit, value: 'Expert', label: 'Guidance' },
    { icon: Zap, value: 'Rapid', label: 'Onboarding' }
  ];

  return (
    <section id="contact" className="py-20 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[hsl(var(--accent-violet))]/5 to-transparent"></div> {/* Use accent-violet for background tint */}
      
      <div className="container mx-auto px-6 relative z-10">
        <motion.div initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }} className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            <span className="gradient-text">Ready to Transform</span><br />Your Trading Operations?
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Connect with our experts to discuss how VegaVelox can elevate your trading performance and give you the competitive edge you need.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <motion.div initial={{ opacity: 0, x: -50 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }} className="glass-effect rounded-xl p-8">
            {isSubmitted ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="flex flex-col items-center justify-center text-center h-full"
              >
                <CheckCircle className="w-16 h-16 text-[hsl(var(--primary-gold))] mb-6" /> {/* Success icon using primary gold */}
                <h3 className="text-2xl font-bold text-white mb-4">Thank You!</h3>
                <p className="text-gray-300 mb-8 max-w-sm">
                  Your message has been sent successfully. We will review it and get back to you as soon as possible.
                </p>
                <Button
                  onClick={() => {
                    setIsSubmitted(false);
                    if(form.current) form.current.reset();
                  }}
                  size="lg"
                  className="bg-transparent border-2 border-[hsl(var(--primary-gold))] text-[hsl(var(--primary-gold))] hover:bg-[hsl(var(--primary-gold))]/10 px-8 py-3 font-semibold rounded-lg"
                >
                  Send Another Message
                </Button>
              </motion.div>
            ) : (
              <>
                <h3 className="text-2xl font-bold mb-6 gradient-text">Get Started Today</h3> {/* Now uses Oxanium font */}
                <form ref={form} onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">Full Name</label>
                      <input type="text" id="name" name="name" className="w-full px-4 py-3 bg-black/20 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-[hsl(var(--primary-gold))]" placeholder="John Doe" required />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">Email Address</label>
                      <input type="email" id="email" name="email" className="w-full px-4 py-3 bg-black/20 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-[hsl(var(--primary-gold))]" placeholder="john@company.com" required />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-gray-300 mb-2">Company</label>
                    <input type="text" id="company" name="company" className="w-full px-4 py-3 bg-black/20 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-[hsl(var(--primary-gold))]" placeholder="Your Company Name" />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">Message</label>
                    <textarea id="message" name="message" rows={4} className="w-full px-4 py-3 bg-black/20 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-[hsl(var(--primary-gold))]" placeholder="Tell us about your trading requirements..." required />
                  </div>
                  {/* Updated button to Aztec Dark to Primary Gold gradient for Send Message, and pulse-animation */}
                  <Button type="submit" size="lg" 
                    className="w-full bg-gradient-to-r from-[hsl(var(--aztec-dark))] to-[hsl(var(--primary-gold))] text-white px-8 py-4 text-lg font-bold rounded-lg transition-all duration-300 shadow-lg hover:shadow-yellow-500/20 hover:brightness-110 pulse-animation"
                  disabled={isSending}>
                    {isSending ? 'Sending...' : <>Send Message <Send className="ml-2 w-5 h-5" /></>}
                  </Button>
                </form>
              </>
            )}
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 50 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }} className="space-y-8">
            <div className="space-y-6">
              {contactInfo.map((info) => (
                <motion.div key={info.title} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="glass-effect rounded-xl p-6 hover:neon-glow">
                  <div className="flex items-start space-x-4">
                    {/* Updated icon background to Aztec Dark to Primary Gold gradient */}
                    <div className="w-12 h-12 bg-gradient-to-br from-[hsl(var(--aztec-dark))] to-[hsl(var(--primary-gold))] rounded-lg flex items-center justify-center flex-shrink-0">
                      <info.icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-white mb-1">{info.title}</h4>
                      <p className="text-[hsl(var(--primary-gold))] font-medium mb-2">{info.details}</p>
                      <p className="text-gray-400 text-sm">{info.description}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }} className="glass-effect rounded-xl p-6">
              <h4 className="text-xl font-bold mb-6 gradient-text">Why Choose VegaVelox?</h4> {/* Now uses Oxanium font */}
              <div className="grid grid-cols-3 gap-4">
                {stats.map((stat) => (
                  <motion.div key={stat.label} initial={{ opacity: 0, scale: 0.8 }} whileInView={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }} viewport={{ once: true }} className="text-center">
                    {/* Updated icon background to Aztec Dark to Primary Gold gradient, matching image */}
                    <div className="w-10 h-10 bg-gradient-to-br from-[hsl(var(--aztec-dark))] to-[hsl(var(--primary-gold))] rounded-lg flex items-center justify-center mx-auto mb-3">
                      <stat.icon className="w-5 h-5 text-white" />
                    </div>
                    <div className="text-2xl font-bold gradient-text mb-1">{stat.value}</div> {/* Now uses Oxanium font */}
                    <div className="text-gray-400 text-sm">{stat.label}</div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>


  );
};

export default Contact;
// --- END OF FILE Contact.jsx ---